// filepath: resources/js/components/pages/CMSPages/FriVia/MyResults/MyQuizAnswersTable.jsx
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridTextSorter,
  dataGridNumberSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useYesNoStringDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';

const LOCAL_STORAGE_KEY = 'myQuizAnswersExpandedRows';

const MyQuizAnswersTable = ({ rows }) => {
  const { t } = useTranslation();

  const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const handleExpandedChange = useCallback((keys) => {
    setExpandedRowKeys(keys);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(keys));
    } catch (e) {
      // ignore persistence errors
    }
  }, []);

  const quizFilter = useDataGridTextFilter('quiz_title');
  const subjectFilter = useDataGridTextFilter('study_subject_name');
  const questionFilter = useDataGridTextFilter('question_text');
  const yesNoFilter = useYesNoStringDataGridFilter('answered_correctly');
  const answerFilter = useDataGridTextFilter('answer_text');
  const correctCountFilter = useDataGridTextFilter('correct_count');

  const columns = useMemo(() => {
    return [
      {
        title: t('Kvíz'),
        dataIndex: 'quiz_title',
        key: 'quiz_title',
        ellipsis: true,
        sorter: dataGridTextSorter('quiz_title'),
        ...quizFilter,
      },
      {
        title: t('Predmet'),
        dataIndex: 'study_subject_name',
        key: 'study_subject_name',
        ellipsis: true,
        sorter: dataGridTextSorter('study_subject_name'),
        ...subjectFilter,
        render: (v) => {
          const str = !v ? '' : String(v);
          return str.trim() !== '' ? str : '-';
        },
      },
      {
        title: t('Počet správnych odpovedí'),
        dataIndex: 'correct_count',
        key: 'correct_count',
        width: 250,
        sorter: dataGridNumberSorter('correct_count'),
        ...correctCountFilter,
      },
    ];
  }, [t, quizFilter, subjectFilter, correctCountFilter]);

  const childColumns = useMemo(() => {
    return [
      {
        title: t('Otázka'),
        dataIndex: 'question_text',
        key: 'question_text',
        ellipsis: true,
        sorter: dataGridTextSorter('question_text'),
        ...questionFilter,
      },
      {
        title: t('Odpoveď'),
        dataIndex: 'answer_text',
        key: 'answer_text',
        ellipsis: true,
        sorter: dataGridTextSorter('answer_text'),
        ...answerFilter,
      },
      {
        title: t('Odpovedal správne'),
        dataIndex: 'answered_correctly',
        sorter: dataGridTextSorter('answered_correctly'),
        width: 250,
        ...yesNoFilter,
        render: (val) => {
          const yesLike = val === 'Yes';
          return yesLike ? t('Áno') : t('Nie');
        },
      },
    ];
  }, [t, questionFilter, answerFilter]);

  return (
    <DataGrid
      permissionsBasic={'quiz-student-answers'}
      uniqueTableId={'quiz-student-answers-myanswers'}
      dataSource={rows}
      columns={columns}
      pagination={false}
      disableSettings
      customExpandedRowRender={(parent) => {
        const children = Array.isArray(parent.nested_answers)
          ? parent.nested_answers
          : [];
        return (
          <DataGrid
            permissionsBasic={undefined}
            dataSource={children.map((c, idx) => ({
              key: `${parent.quiz_id}-${idx}`,
              ...c,
            }))}
            columns={childColumns}
            uniqueTableId={`quiz-student-answers-myanswers-child-${parent.quiz_id}`}
            pagination={false}
            disableSettings
          />
        );
      }}
      disableExpandRow={(record) =>
        !record.nested_answers || record.nested_answers.length === 0
      }
      expandedRowKeys={expandedRowKeys}
      onExpandedRowsChange={handleExpandedChange}
    />
  );
};

MyQuizAnswersTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default MyQuizAnswersTable;
